import { Banknote, FileCheck2, FileText, Landmark, Undo2 } from 'lucide-react';
import { createElement } from 'react';
import type { LifecycleEvent } from '../../../components/LifecycleTimeline';
import { formatDateOnly } from '../../../lib/formatDate';
import type { DisbursementRequestDetail } from '../disbursementRequestTypes';

/** 16px, which is what `<LifecycleTimeline>`'s rail is sized for. */
const glyph = (icon: typeof FileText) => createElement(icon, { className: 'h-4 w-4' });

/**
 * A request's life cycle, DERIVED from the stamps each step already writes — nothing here is
 * stored, so there is no second history to fall out of step with the audit trail.
 *
 * <b>`!= null` throughout</b>: every field came off the wire, and an absent one is omitted.
 *
 * @param receivedByName the person who took the voucher back, resolved by the page — null where
 *   the reader cannot resolve names, and the event then carries the recording account alone.
 */
export function disbursementRequestLifecycle(
  request: DisbursementRequestDetail,
  receivedByName: string | null,
): LifecycleEvent[] {
  const events: LifecycleEvent[] = [
    {
      key: 'raised',
      label: 'Created',
      at: request.createdAt,
      icon: glyph(FileText),
      tone: 'slate',
      actor: request.createdByName,
    },
  ];

  if (request.cashProcessedAt != null) {
    events.push({
      key: 'processing',
      label: 'For Accounting Processing',
      at: request.cashProcessedAt,
      icon: glyph(Landmark),
      tone: 'blue',
      actor: request.cashProcessedByName,
    });
  }

  if (request.voucherReceivedAt != null) {
    const parts = [
      request.voucherNumber != null ? `Voucher ${request.voucherNumber}` : null,
      request.voucherDate != null ? `dated ${formatDateOnly(request.voucherDate)}` : null,
      receivedByName != null ? `received by ${receivedByName}` : null,
    ].filter((p): p is string => p != null);

    events.push({
      key: 'voucher',
      label: 'Voucher received',
      at: request.voucherReceivedAt,
      icon: glyph(FileCheck2),
      tone: 'blue',
      actor: request.voucherReceivedByName,
      detail: parts.length > 0 ? parts.join(', ') : undefined,
    });
  }

  if (request.disbursedAt != null) {
    events.push({
      key: 'disbursed',
      label: 'Disbursed',
      at: request.disbursedAt,
      icon: glyph(Banknote),
      tone: 'emerald',
      actor: request.disbursedByName,
      detail: [
        request.disbursedOn != null ? `Released on ${formatDateOnly(request.disbursedOn)}` : null,
        request.disbursementReference != null ? `reference ${request.disbursementReference}` : null,
      ]
        .filter((p): p is string => p != null)
        .join(', ') || undefined,
    });
  }

  if (request.returnedAt != null) {
    events.push({
      key: 'returned',
      label: request.returnedFromStatusLabel != null ? `Returned from ${request.returnedFromStatusLabel}` : 'Returned',
      at: request.returnedAt,
      icon: glyph(Undo2),
      tone: 'red',
      actor: request.returnedByName,
      detail: request.returnReason ?? undefined,
    });
  }

  return events;
}
